import { useEffect, useRef } from "react";
import { AppBridge, PostMessageTransport } from "@modelcontextprotocol/ext-apps/app-bridge";
import type { CallToolRequest, CallToolResult } from "@modelcontextprotocol/sdk/types.js";
import { apiMutate } from "~/utils/apiClient";

const { VITE_ANALYSIS_AGENT_API, VITE_CUSTOMER } = import.meta.env;

const HOST_INFO = { name: "manifold-nextgen", version: "1.0.0" };

interface McpAppPaneFrameProps {
  // Exactly one is set — `html` for legacy inline `ui://` bundles, `url` for resources
  // migrated to serve a URL (see `fetchMcpAppResource`).
  html: string | null;
  url: string | null;
  title: string;
  serverId: string;
  // Latest render state; pushed into the iframe on init and on every change.
  state: unknown;
  // Called with the fresh state after a panel-initiated tool call, so the pane
  // can keep the tab (title, dedupe key) in sync with edits made in the panel.
  onLocalUpdate: (state: unknown) => void;
}

function currentThemeMode(): "light" | "dark" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/**
 * Wraps a panel payload as an MCP `CallToolResult`. `structuredContent` must be an object, so a
 * non-object payload travels only as the JSON text fallback.
 */
function toCallToolResult(content: unknown): CallToolResult {
  const isObject = typeof content === "object" && content !== null && !Array.isArray(content);
  return {
    content: [{ type: "text", text: JSON.stringify(content) }],
    ...(isObject ? { structuredContent: content as Record<string, unknown> } : {}),
  };
}

/**
 * A pane-hosted MCP-app iframe, driven through an `AppBridge`. It pushes `state` into the panel
 * in place, so one iframe follows the agent's running edits rather than remounting per tool
 * call, and proxies the panel's own tool calls to the analysis-agent. See `apps/demo-mcp` for
 * the reference panel.
 */
export function McpAppPaneFrame({
  html,
  url,
  title,
  serverId,
  state,
  onLocalUpdate,
}: McpAppPaneFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<AppBridge | null>(null);
  const readyRef = useRef(false);
  // Read inside the bridge's callbacks without rebuilding it on every change.
  const stateRef = useRef(state);
  stateRef.current = state;
  const serverIdRef = useRef(serverId);
  serverIdRef.current = serverId;
  const onLocalUpdateRef = useRef(onLocalUpdate);
  onLocalUpdateRef.current = onLocalUpdate;

  // Rebuilt per document because a new url/html reloads the frame.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) {
      return;
    }
    const bridge = new AppBridge(null, HOST_INFO, { serverTools: {} });

    bridge.oncalltool = async (params: CallToolRequest["params"]) => {
      const result = await apiMutate<unknown>(
        `${VITE_ANALYSIS_AGENT_API}/mcp-resources/call`,
        "POST",
        {
          server_id: serverIdRef.current,
          tool: params.name,
          arguments: params.arguments ?? {},
        }
      );
      onLocalUpdateRef.current(result);
      return toCallToolResult(result);
    };

    // `sendToolInput` is required before any `sendToolResult`.
    bridge.addEventListener("initialized", () => {
      readyRef.current = true;
      void bridge.sendToolInput({ arguments: {} });
      void bridge.sendToolResult(toCallToolResult(stateRef.current));
      void bridge.sendHostContextChange({ theme: currentThemeMode(), customer: VITE_CUSTOMER });
    });

    bridgeRef.current = bridge;
    void bridge.connect(new PostMessageTransport(iframe.contentWindow, iframe.contentWindow));

    // Nextgen's dark-mode toggle sets this class on <html> (see ManifoldNextGenLayout).
    const observer = new MutationObserver(() => {
      if (readyRef.current) {
        void bridge.sendHostContextChange({ theme: currentThemeMode(), customer: VITE_CUSTOMER });
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => {
      observer.disconnect();
      readyRef.current = false;
      bridgeRef.current = null;
      void bridge.close();
    };
  }, [url, html]);

  useEffect(() => {
    if (readyRef.current) {
      void bridgeRef.current?.sendToolResult(toCallToolResult(state));
    }
  }, [state]);

  return (
    <iframe
      ref={iframeRef}
      {...(url !== null ? { src: url } : { srcDoc: html ?? undefined })}
      sandbox="allow-scripts allow-top-navigation-by-user-activation"
      title={title}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        display: "block",
      }}
    />
  );
}
